/**
 * SessionReviewTip.jsx
 * Post-session review and tip page.
 * Accessible via /dashboard/bookings/review/:bookingId
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, ArrowLeft, Heart, ExternalLink, CreditCard, DollarSign } from "lucide-react";
import { apiFetch } from "./api";
import StripeInAppCheckout from "../../components/StripeInAppCheckout";
import { isMobileBuild } from "@/lib/mobileBuild";
import UpgradeOnWebNotice from "@/components/UpgradeOnWebNotice";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,500;0,600;1,400&family=Outfit:wght@300;400;500;600&display=swap');

  .srt-page {
    background: #f8f6f2;
    min-height: 100vh;
  }

  .srt-root {
    --forest: #1b3a2d;
    --forest-light: #2a5a44;
    --gold: #c0900a;
    --gold-bg: #fef9ea;
    --cream: #f8f6f2;
    --surface: #ffffff;
    --ink: #1a1a16;
    --ink-mid: #6a6a60;
    --ink-light: #9a9a90;
    --border: #e4e2de;
    --copper: #b8734a;
    font-family: 'Outfit', sans-serif;
    max-width: 640px;
    margin: 0 auto;
    padding: 28px 24px;
  }

  .srt-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    color: var(--ink-light);
    font-size: 13px;
    cursor: pointer;
    margin-bottom: 20px;
    padding: 0;
    font-family: 'Outfit', sans-serif;
  }
  .srt-back:hover { color: var(--ink); }

  .srt-title {
    font-family: 'Crimson Pro', serif;
    font-size: 28px;
    font-weight: 500;
    color: var(--ink);
    margin: 0 0 4px;
  }

  .srt-subtitle {
    font-size: 14px;
    color: var(--ink-mid);
    margin: 0 0 24px;
  }

  .srt-expiry {
    font-size: 12px;
    color: var(--copper);
    margin: 0 0 24px;
    background: var(--gold-bg);
    padding: 8px 14px;
    border-radius: 8px;
    border-left: 3px solid var(--gold);
  }

  /* ── Section cards ── */
  .srt-section {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 24px;
    margin-bottom: 20px;
  }

  .srt-section-title {
    font-family: 'Crimson Pro', serif;
    font-size: 20px;
    font-weight: 500;
    color: var(--ink);
    margin: 0 0 4px;
  }

  .srt-section-desc {
    font-size: 13px;
    color: var(--ink-light);
    margin: 0 0 16px;
  }

  /* ── Star rating ── */
  .srt-stars {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }

  .srt-star {
    cursor: pointer;
    transition: transform 0.15s, color 0.15s;
    color: var(--border);
  }
  .srt-star:hover { transform: scale(1.15); }
  .srt-star.active { color: var(--gold); }

  .srt-textarea {
    width: 100%;
    min-height: 100px;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    padding: 12px 14px;
    font-family: 'Outfit', sans-serif;
    font-size: 14px;
    color: var(--ink);
    resize: vertical;
    outline: none;
    transition: border-color 0.15s;
  }
  .srt-textarea:focus { border-color: var(--forest-light); }

  .srt-submit-btn {
    margin-top: 14px;
    padding: 12px 24px;
    background: var(--forest);
    color: #fff;
    border: none;
    border-radius: 10px;
    font-family: 'Outfit', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
    width: 100%;
  }
  .srt-submit-btn:hover { opacity: 0.9; }
  .srt-submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── Tip section ── */
  .srt-tip-presets {
    display: flex;
    gap: 10px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }

  .srt-tip-btn {
    padding: 10px 20px;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    background: var(--cream);
    font-family: 'Outfit', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: var(--ink-mid);
    cursor: pointer;
    transition: all 0.15s;
    flex: 1;
    min-width: 80px;
    text-align: center;
  }
  .srt-tip-btn:hover { border-color: var(--forest-light); color: var(--ink); }
  .srt-tip-btn.active {
    border-color: var(--forest);
    background: var(--surface);
    color: var(--forest);
    font-weight: 600;
  }

  .srt-custom-amount {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
  }

  .srt-amount-input {
    flex: 1;
    padding: 10px 14px 10px 30px;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    font-family: 'Outfit', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: var(--ink);
    outline: none;
    position: relative;
  }
  .srt-amount-input:focus { border-color: var(--forest-light); }

  .srt-amount-wrap {
    position: relative;
    flex: 1;
  }
  .srt-amount-wrap .dollar-sign {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--ink-light);
    font-size: 16px;
    font-weight: 600;
  }

  .srt-notice {
    font-size: 12px;
    color: var(--ink-light);
    font-style: italic;
    margin: 0 0 12px;
  }

  .srt-fee-notice {
    font-size: 11px;
    color: var(--copper);
    margin: 8px 0 0;
  }

  /* ── External payment ── */
  .srt-external {
    background: var(--cream);
    border-radius: 10px;
    padding: 14px;
    margin-bottom: 14px;
  }

  .srt-external-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 8px;
  }

  .srt-external-links {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .srt-ext-link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 13px;
    color: var(--forest);
    font-weight: 500;
    text-decoration: none;
    transition: background 0.15s;
  }
  .srt-ext-link:hover { background: var(--gold-bg); }

  .srt-or-divider {
    text-align: center;
    color: var(--ink-light);
    font-size: 12px;
    margin: 14px 0;
    position: relative;
  }
  .srt-or-divider::before,
  .srt-or-divider::after {
    content: '';
    position: absolute;
    top: 50%;
    width: calc(50% - 20px);
    height: 1px;
    background: var(--border);
  }
  .srt-or-divider::before { left: 0; }
  .srt-or-divider::after { right: 0; }

  /* Payment method toggle */
  .srt-pay-toggle {
    display: flex;
    gap: 10px;
    margin-bottom: 14px;
  }

  .srt-pay-opt {
    flex: 1;
    padding: 10px 14px;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    background: var(--cream);
    font-size: 13px;
    font-weight: 500;
    color: var(--ink-mid);
    cursor: pointer;
    text-align: center;
    transition: all 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
  .srt-pay-opt:hover { border-color: var(--forest-light); }
  .srt-pay-opt.active {
    border-color: var(--forest);
    background: var(--surface);
    color: var(--forest);
    font-weight: 600;
  }

  .srt-success {
    text-align: center;
    padding: 24px;
  }

  .srt-success-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #e8f5e8;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    color: var(--forest-light);
  }

  .srt-success h3 {
    font-family: 'Crimson Pro', serif;
    font-size: 22px;
    color: var(--ink);
    margin: 0 0 6px;
  }

  .srt-success p {
    font-size: 14px;
    color: var(--ink-mid);
    margin: 0;
  }
`;

export default function SessionReviewTip() {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [canReviewData, setCanReviewData] = useState(null);
  const [tipInfo, setTipInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Review state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewBody, setReviewBody] = useState("");
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // Tip state
  const [tipPreset, setTipPreset] = useState(null); // "10", "15", "20", "custom"
  const [customTipAmount, setCustomTipAmount] = useState("");
  const [payMethod, setPayMethod] = useState("new"); // "saved" or "new"
  const [tipSubmitted, setTipSubmitted] = useState(false);
  const [tipClientSecret, setTipClientSecret] = useState(null);
  const [tipProcessing, setTipProcessing] = useState(false);
  const [tipId, setTipId] = useState(null);
  const [tipCharged, setTipCharged] = useState(null);
  const [tipProcFee, setTipProcFee] = useState(0);
  const [tipError, setTipError] = useState(null);

  useEffect(() => {
    Promise.all([
      apiFetch(`/session-feedback/reviews/can-review/${bookingId}`).catch(() => ({ can_review: false })),
      apiFetch(`/session-feedback/tip-info/${bookingId}`).catch(() => null),
    ]).then(([reviewData, tip]) => {
      setCanReviewData(reviewData);
      setTipInfo(tip);
      if (tip?.already_tipped) setTipSubmitted(true);
      if (!reviewData?.can_review && reviewData?.reason === "already_reviewed") setReviewSubmitted(true);
      setLoading(false);
    });
  }, [bookingId]);

  const sessionPrice = tipInfo?.session_price || 0;
  const tipAmount = tipPreset === "custom"
    ? parseFloat(customTipAmount) || 0
    : tipPreset ? Math.round(sessionPrice * (parseInt(tipPreset) / 100) * 100) / 100 : 0;

  const externalPayment = tipInfo?.external_payment;
  const hasExternal = externalPayment && (externalPayment.venmo_handle || externalPayment.cashapp_handle || externalPayment.paypal_handle);

  const submitReview = async () => {
    if (!rating) return;
    setReviewSubmitting(true);
    try {
      await apiFetch("/session-feedback/reviews", {
        method: "POST",
        body: JSON.stringify({ booking_id: bookingId, rating, body: reviewBody || undefined }),
      });
      setReviewSubmitted(true);
    } catch (e) {
      console.error(e);
    }
    setReviewSubmitting(false);
  };

  const submitTipViaStripe = async () => {
    if (tipAmount <= 0) return;
    setTipProcessing(true);
    setTipError(null);
    try {
      const res = await apiFetch("/session-feedback/tips", {
        method: "POST",
        body: JSON.stringify({
          booking_id: bookingId,
          amount: tipAmount,
          use_saved_card: payMethod === "saved",
        }),
      });
      setTipId(res.tip_id);
      setTipCharged(res.charged_amount ?? null);
      setTipProcFee(res.processing_fee ?? 0);
      if (res.requires_action && res.client_secret) {
        setTipClientSecret(res.client_secret);
      } else {
        setTipSubmitted(true);
      }
    } catch (e) {
      setTipError(e?.message || "We couldn't start that tip. Please try again.");
    }
    setTipProcessing(false);
  };

  const finishTip = async () => {
    try {
      await apiFetch(`/session-feedback/tips/${tipId}/confirm`, { method: "POST" });
    } catch (e) {
      // Card cleared — recording the outcome must not block the thank-you
    }
    setTipSubmitted(true);
  };

  // Calculate expiry display
  const expiresAt = canReviewData?.expires_at ? new Date(canReviewData.expires_at) : null;
  const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - new Date()) / 86400000)) : null;

  if (loading) {
    return (<><style>{styles}</style><div className="srt-page"><div className="srt-root"><p style={{ color: '#9a9a90', textAlign: 'center', padding: 40 }}>Loading...</p></div></div></>);
  }

  return (
    <>
      <style>{styles}</style>
      <div className="srt-page">
      <div className="srt-root">
        <button className="srt-back" onClick={() => navigate("/dashboard/bookings")} data-testid="review-back-btn">
          <ArrowLeft size={14} /> Back to bookings
        </button>

        <h1 className="srt-title">Session Feedback</h1>
        <p className="srt-subtitle">{tipInfo?.session_title || "Your Session"} with {tipInfo?.practitioner_name || "Practitioner"}</p>

        {daysLeft !== null && daysLeft > 0 && (
          <div className="srt-expiry">
            You have <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong> left to leave your review and tip.
          </div>
        )}

        {/* ── Review Section ── */}
        <div className="srt-section" data-testid="review-section">
          <h2 className="srt-section-title">Leave a Review</h2>
          <p className="srt-section-desc">Your review is public and helps other members find the right practitioner.</p>

          {reviewSubmitted ? (
            <div className="srt-success">
              <div className="srt-success-icon"><Star size={24} fill="currentColor" /></div>
              <h3>Thank you for your review!</h3>
              <p>Your feedback helps our community grow.</p>
            </div>
          ) : canReviewData?.can_review ? (
            <>
              <div className="srt-stars" data-testid="review-stars">
                {[1,2,3,4,5].map(s => (
                  <Star
                    key={s}
                    size={32}
                    className={`srt-star ${(hoverRating || rating) >= s ? 'active' : ''}`}
                    fill={(hoverRating || rating) >= s ? 'currentColor' : 'none'}
                    onMouseEnter={() => setHoverRating(s)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(s)}
                    data-testid={`star-${s}`}
                  />
                ))}
              </div>
              <textarea
                className="srt-textarea"
                placeholder="Share your experience (optional)..."
                value={reviewBody}
                onChange={e => setReviewBody(e.target.value)}
                data-testid="review-body"
              />
              <button
                className="srt-submit-btn"
                onClick={submitReview}
                disabled={!rating || reviewSubmitting}
                data-testid="submit-review-btn"
              >
                {reviewSubmitting ? "Submitting..." : "Submit Review"}
              </button>
            </>
          ) : (
            <p style={{ color: 'var(--ink-light)', fontSize: 14 }}>
              {canReviewData?.reason === "expired" ? "The review period has expired." : "Review not available."}
            </p>
          )}
        </div>

        {/* ── Tip Section ── */}
        {!tipInfo?.already_tipped && (
          <div className="srt-section" data-testid="tip-section">
            <h2 className="srt-section-title">Tip Your Practitioner</h2>
            <p className="srt-notice">Tipping is not mandatory, but it is appreciated.</p>

            {tipSubmitted ? (
              <div className="srt-success">
                <div className="srt-success-icon"><Heart size={24} /></div>
                <h3>Tip sent!</h3>
                <p>Your practitioner will appreciate your generosity.</p>
              </div>
            ) : tipClientSecret ? (
              <StripeInAppCheckout
                clientSecret={tipClientSecret}
                amount={tipCharged ?? tipAmount}
                netAmount={tipAmount}
                processingFee={tipProcFee}
                label={`Tip for ${tipInfo?.practitioner_name}`}
                onSuccess={finishTip}
                onCancel={() => setTipClientSecret(null)}
              />
            ) : (
              <>
                {/* External payment options */}
                {hasExternal && (
                  <>
                    <div className="srt-external">
                      <p className="srt-external-title">Send directly (no processing fees):</p>
                      <div className="srt-external-links">
                        {externalPayment.venmo_handle && (
                          <a href={`https://venmo.com/${externalPayment.venmo_handle}`} target="_blank" rel="noopener noreferrer" className="srt-ext-link" data-testid="tip-venmo">
                            <ExternalLink size={14} /> Venmo: @{externalPayment.venmo_handle}
                          </a>
                        )}
                        {externalPayment.cashapp_handle && (
                          <a href={`https://cash.app/${externalPayment.cashapp_handle}`} target="_blank" rel="noopener noreferrer" className="srt-ext-link" data-testid="tip-cashapp">
                            <ExternalLink size={14} /> Cash App: {externalPayment.cashapp_handle}
                          </a>
                        )}
                        {externalPayment.paypal_handle && (
                          <a href={`https://paypal.me/${externalPayment.paypal_handle}`} target="_blank" rel="noopener noreferrer" className="srt-ext-link" data-testid="tip-paypal">
                            <ExternalLink size={14} /> PayPal: {externalPayment.paypal_handle}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="srt-or-divider">or</div>
                  </>
                )}

                {/* Stripe-based tip */}
                <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', margin: '0 0 10px' }}>
                  {sessionPrice > 0 ? 'Choose a tip amount:' : 'Enter a tip amount:'}
                </p>

                {sessionPrice > 0 && (
                  <div className="srt-tip-presets">
                    {["10","15","20"].map(pct => (
                      <button
                        key={pct}
                        className={`srt-tip-btn ${tipPreset === pct ? 'active' : ''}`}
                        onClick={() => { setTipPreset(pct); setCustomTipAmount(""); }}
                        data-testid={`tip-${pct}pct`}
                      >
                        {pct}%
                        <br /><span style={{ fontSize: 11, opacity: 0.7 }}>${(sessionPrice * parseInt(pct) / 100).toFixed(2)}</span>
                      </button>
                    ))}
                    <button
                      className={`srt-tip-btn ${tipPreset === 'custom' ? 'active' : ''}`}
                      onClick={() => setTipPreset('custom')}
                      data-testid="tip-custom"
                    >
                      Custom
                    </button>
                  </div>
                )}

                {(tipPreset === 'custom' || sessionPrice === 0) && (
                  <div className="srt-custom-amount">
                    <div className="srt-amount-wrap">
                      <span className="dollar-sign">$</span>
                      <input
                        className="srt-amount-input"
                        type="number"
                        min="1"
                        step="0.01"
                        placeholder="0.00"
                        value={customTipAmount}
                        onChange={e => setCustomTipAmount(e.target.value)}
                        data-testid="tip-custom-amount"
                      />
                    </div>
                  </div>
                )}

                {isMobileBuild() ? (
                  <div style={{ marginTop: 16 }}>
                    <UpgradeOnWebNotice
                      title="Send your tip on our website"
                      description="Card tips are handled on our website. Open it there to send your thanks — or use one of the direct options above."
                      ctaLabel="Tip on Website"
                      path={`/dashboard/bookings/review/${bookingId}`}
                      testId="tip-web-notice"
                      showQr={false}
                    />
                  </div>
                ) : tipAmount > 0 && (
                  <>
                    <p className="srt-fee-notice">
                      Card payments add a 3% + $0.30 processing fee, so your practitioner receives the full tip.
                    </p>
                    {tipError && (
                      <p className="srt-fee-notice" style={{ color: "#a12b2b" }} data-testid="tip-error">
                        {tipError}
                      </p>
                    )}
                    <button
                      className="srt-submit-btn"
                      onClick={submitTipViaStripe}
                      disabled={tipProcessing}
                      data-testid="submit-tip-btn"
                      style={{ marginTop: 14 }}
                    >
                      {tipProcessing ? "Processing..." : `Send $${tipAmount.toFixed(2)} Tip`}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {tipSubmitted && tipInfo?.already_tipped && (
          <div className="srt-section">
            <div className="srt-success">
              <div className="srt-success-icon"><Heart size={24} /></div>
              <h3>You already tipped for this session</h3>
              <p>Thank you for your generosity!</p>
            </div>
          </div>
        )}
      </div>
      </div>
    </>
  );
}
